import { useEffect } from "react";

// Lightweight SEO hook — sets <title>, meta description, and a JSON-LD script.
// No external deps. Cleans up on unmount so we don't leak state between pages.
export function useSeo({ title, description, jsonLd, canonical } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const setMeta = (selector, attrs, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };

    if (title) document.title = title;

    const metaDesc = description ? setMeta('meta[name="description"]', { name: "description" }, description) : null;
    const metaOgTitle = title ? setMeta('meta[property="og:title"]', { property: "og:title" }, title) : null;
    const metaOgDesc = description ? setMeta('meta[property="og:description"]', { property: "og:description" }, description) : null;
    const metaOgType = setMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    const metaTwCard = setMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");

    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
      // Leave meta tags in place — they'll be overwritten by the next page.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, JSON.stringify(jsonLd)]);
}

// LocalBusiness schema reused across pages.
export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Tombs Electronic Repair LLC",
  image: "https://tombselectronicsrepair.com/logo.png",
  "@id": "https://tombselectronicsrepair.com",
  url: "https://tombselectronicsrepair.com",
  telephone: "+1-607-953-1221",
  email: "noah@tombselectronicsrepair.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "67 Grand St",
    addressLocality: "Susquehanna",
    addressRegion: "PA",
    postalCode: "18847",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 41.9462, longitude: -75.6018 },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "18:00", closes: "23:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "09:00", closes: "21:00" },
  ],
  areaServed: [
    { "@type": "City", name: "Susquehanna, PA" },
    { "@type": "City", name: "Lanesboro, PA" },
    { "@type": "City", name: "Hallstead, PA" },
    { "@type": "City", name: "Great Bend, PA" },
    { "@type": "City", name: "Nicholson, PA" },
    { "@type": "City", name: "Hop Bottom, PA" },
    { "@type": "City", name: "Kirkwood, NY" },
    { "@type": "City", name: "Conklin, NY" },
  ],
};
